/**
 * Premium-Support-Chat — Floating Bubble
 *
 * Spec §3.2: glass-surface, ~64 px, rf-fux-yellow.svg als Inhalt.
 * Drei-Schichten-Sicherheit (Spec §2.4):
 *   - Schicht 1 (Layout): wird nur in <AuthenticatedLayout> gemountet
 *   - Schicht 2 (Hook): rendert null wenn useChatAccess.hasAccess === false
 *   - Schicht 3 (Backend): requireChatAccess-Middleware blockiert API-Calls
 *
 * Zwei Verhaltensmodi:
 *
 *   USER-MODUS:
 *     - Klick toggelt das Mini-Panel (Desktop) / Bottom-Sheet (Mobile)
 *     - Status-Dot zeigt Verfügbarkeit des Supports (grün/orange/grau)
 *     - Glow nur wenn Support online
 *
 *   ADMIN-MODUS:
 *     - Klick navigiert zu /admin-dashboard/support (Shortcut)
 *     - Badge zeigt Anzahl Kunden mit ungelesenen Nachrichten
 *     - Auf Mobile: weiterhin Panel-Sheet mit Master-Detail (Liste → Thread)
 *       (Admin braucht Mobile-Workflow zum Antworten von unterwegs)
 *     - Status-Dot wird durch Badge ersetzt — Admins interessiert die eigene
 *       Verfügbarkeit weniger als die offenen Kundenanfragen
 *
 * Premium-Details:
 *   - Position: fixed bottom-right, 16 px Mobile / 24 px Desktop
 *   - Inhalt: Brand-Icon, kein generisches Sprechblasen-Symbol
 *   - Hover: scale 1.04, 300 ms ease-out
 *   - z-40 (über Inhalt, unter Modal/Cookie/Toast)
 *   - Reduced-Motion-respektierend
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatAccess } from '../../hooks/useChatAccess';
import { useChat } from '../../contexts/ChatContext';
import { useSupportAvailability } from '../../hooks/useSupportAvailability';
import { useAdminBadge } from '../../hooks/useAdminBadge';
import { useUserUnreadBadge } from '../../hooks/useUserUnreadBadge';
import { getImageUrl } from '../../utils/imageUtils';

const FOX_ICON = '/images/branding/rf-fux-yellow.svg';

function getDotColorClass(indicator: 'green' | 'orange' | 'gray' | undefined): string {
  if (indicator === 'green') return 'bg-emerald-400';
  if (indicator === 'orange') return 'bg-amber-400';
  return 'bg-gray-400';
}

/** True wenn Viewport-Breite Desktop entspricht (md+ Breakpoint = 768 px). */
function useIsDesktopWidth(): boolean {
  const [isDesktop, setIsDesktop] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 768px)').matches;
  });
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent): void => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

export function ChatBubble(): React.ReactElement | null {
  const access = useChatAccess();
  const { isPanelOpen, togglePanel } = useChat();
  const navigate = useNavigate();
  const isDesktop = useIsDesktopWidth();

  const isAdmin = access.planSlug === 'admin';

  // Admin-spezifisch: Badge mit Kunden-Anzahl. Nur aktiv wenn Admin.
  const adminBadge = useAdminBadge({ enabled: access.hasAccess && isAdmin });

  // User-spezifisch: Anzahl ungelesener Admin-Antworten. Nur aktiv für Nicht-Admin.
  const userBadge = useUserUnreadBadge({ enabled: access.hasAccess && !isAdmin });

  // User-spezifisch: Verfügbarkeits-Status für Bubble-Glow + Dot.
  // Auch für Admin geladen (kann er nutzen wenn er die Bubble auf Mobile öffnet),
  // aber prominenter ist bei Admins der Badge.
  const availability = useSupportAvailability({ enabled: access.hasAccess });
  const status = availability.data;
  const isActiveNow = status?.status === 'active_now';
  const isOnline = status?.indicator === 'green';

  // Schicht 2: Render-Gate. Kein DOM-Element für nicht-berechtigte User.
  if (!access.hasAccess) {
    return null;
  }

  // Glow-Klassen: nur wenn aktuell erreichbar (grün) ODER ungelesene Nachrichten
  // anstehen. Bei ungelesenen ist der Glow stärker — visueller Pull.
  const hasPendingForUser = !isAdmin && userBadge.count > 0;
  const hasPendingForAdmin = isAdmin && adminBadge.count > 0;
  const glowBase =
    hasPendingForAdmin || hasPendingForUser
      ? 'shadow-[0_0_28px_rgba(241,196,15,0.32)] hover:shadow-[0_0_36px_rgba(241,196,15,0.42)]'
      : isActiveNow
        ? 'shadow-[0_0_28px_rgba(241,196,15,0.28)] hover:shadow-[0_0_36px_rgba(241,196,15,0.38)]'
        : isOnline
          ? 'shadow-[0_0_24px_rgba(241,196,15,0.18)] hover:shadow-[0_0_32px_rgba(241,196,15,0.28)]'
          : '';

  // Click-Handler je nach Modus:
  //   - Admin Desktop:  navigate zur Inbox-Seite (Shortcut)
  //   - Admin Mobile:   togglePanel (Master-Detail-Sheet)
  //   - User immer:     togglePanel
  const handleClick = (): void => {
    if (isAdmin && isDesktop) {
      navigate('/admin-dashboard/blog/premium-support');
      return;
    }
    togglePanel();
  };

  // Aria-Label kontextabhängig
  const ariaLabel = isAdmin
    ? adminBadge.count > 0
      ? `Priority Support Inbox öffnen — ${adminBadge.count} ${adminBadge.count === 1 ? 'Kunde wartet' : 'Kunden warten'}`
      : 'Priority Support Inbox öffnen'
    : userBadge.count > 0
      ? `Priority Support öffnen — ${userBadge.count} neue ${userBadge.count === 1 ? 'Nachricht' : 'Nachrichten'}`
      : status?.label
        ? `Priority Support öffnen — ${status.label}`
        : 'Priority Support öffnen';

  const titleText = isAdmin
    ? adminBadge.count > 0
      ? `Priority Support · ${adminBadge.count} ${adminBadge.count === 1 ? 'Kunde wartet' : 'Kunden warten'}`
      : 'Priority Support Inbox'
    : userBadge.count > 0
      ? `Priority Support · ${userBadge.count} neue ${userBadge.count === 1 ? 'Nachricht' : 'Nachrichten'}`
      : status?.label
        ? `Priority Support · ${status.label}`
        : 'Priority Support öffnen';

  return (
    <button
      type="button"
      onClick={handleClick}
      title={titleText}
      aria-label={ariaLabel}
      aria-expanded={isAdmin && isDesktop ? undefined : isPanelOpen}
      aria-controls={isAdmin && isDesktop ? undefined : 'chat-panel'}
      className={`
        fixed
        bottom-4 right-4 sm:bottom-6 sm:right-6
        z-40
        w-12 h-12 sm:w-16 sm:h-16
        rounded-full
        glass-surface
        flex items-center justify-center
        cursor-pointer
        transition-all duration-300 ease-out
        hover:scale-[1.04]
        focus:outline-none focus:ring-2 focus:ring-brand/50 focus:ring-offset-2 focus:ring-offset-gray-950
        motion-reduce:transition-none motion-reduce:hover:scale-100
        ${glowBase}
      `}
    >
      <img
        src={getImageUrl(FOX_ICON)}
        alt=""
        aria-hidden="true"
        className="w-5 h-5 sm:w-7 sm:h-7 object-contain pointer-events-none"
      />

      {/* Drei mutually-exclusive Visuals oben rechts:
          1. Admin mit ungelesenen → goldenes Badge mit Kunden-Anzahl
          2. User mit ungelesenen Antworten → goldenes Badge mit Nachrichten-Anzahl
          3. User ohne ungelesenes → Status-Dot (Verfügbarkeit)
          Admin ohne ungelesenes → nichts (Admin braucht den Status-Dot nicht). */}
      {isAdmin && adminBadge.count > 0 && (
        <span
          aria-hidden="true"
          className="
            absolute -top-0.5 -right-0.5
            min-w-[16px] h-4 px-1 sm:min-w-[20px] sm:h-5
            rounded-full
            bg-brand text-gray-950
            text-[9px] sm:text-[10px] font-semibold font-mono
            flex items-center justify-center
            ring-2 ring-gray-100 dark:ring-gray-800
          "
        >
          {adminBadge.count > 99 ? '99+' : adminBadge.count}
        </span>
      )}

      {!isAdmin && userBadge.count > 0 && (
        <span
          aria-hidden="true"
          className="
            absolute -top-0.5 -right-0.5
            min-w-[16px] h-4 px-1 sm:min-w-[20px] sm:h-5
            rounded-full
            bg-brand text-gray-950
            text-[9px] sm:text-[10px] font-semibold font-mono
            flex items-center justify-center
            ring-2 ring-gray-100 dark:ring-gray-800
          "
        >
          {userBadge.count > 99 ? '99+' : userBadge.count}
        </span>
      )}

      {!isAdmin && userBadge.count === 0 && status && (
        <span
          aria-hidden="true"
          className={`
            absolute bottom-0.5 right-0.5
            w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full
            ring-2 ring-gray-100 dark:ring-gray-800
            ${getDotColorClass(status.indicator)}
            ${isActiveNow ? 'animate-pulse motion-reduce:animate-none' : ''}
          `}
        />
      )}
    </button>
  );
}
